/**
 * Módulo: Reconciliation - Recálculo Cronológico Otimizado
 * Arquivo: js/pages/suprimentos/estoque/reconciliation.js
 * 
 * RESPONSABILIDADE:
 * - Recalcular saldo e custo médio de um item em ordem cronológica
 * - Otimizado: busca TODOS os dados de uma vez, calcula em memória
 * - Suporta lançamentos retroativos (recalcula da data alterada em diante)
 */

window.FazendaRP = window.FazendaRP || {};
window.FazendaRP.Estoque = window.FazendaRP.Estoque || {}; // CORREÇÃO: Mantém objeto existente
window.FazendaRP.Estoque.Reconciliation = {

    /**
     * Recalcula estoque de um item em ordem cronológica
     * 
     * IMPORTANTE: Este é o CORAÇÃO do sistema!
     * - NON-BLOCKING: Busca todos os dados de uma vez
     * - MEMORY-BASED: Calcula em memória sem queries adicionais
     * - CHRONOLOGICAL: Respeita data_movimentacao (não created_at)
     * 
     * @param {string} estoqueId - ID do registro de estoque
     * @returns {Promise<Object>} { success, saldo, custo, timeline }
     */
    recalcularEstoque: async function (estoqueId) {
        console.log(`\n🔄 [Reconciliation] Iniciando recálculo para estoque ID: ${estoqueId}`);
        const startTime = performance.now();

        try {
            const DataService = window.FazendaRP.Estoque.DataService;
            const Calculator = window.FazendaRP.Estoque.Calculator;

            // 1. Buscar TODAS as movimentações em ordem cronológica
            const movimentacoes = await DataService.buscarMovimentacoes(estoqueId);

            if (!movimentacoes || movimentacoes.length === 0) {
                console.warn(`[Reconciliation] Nenhuma movimentação encontrada. Zerando estoque.`);
                await DataService.atualizarEstoque(estoqueId, 0, 0);
                return { success: true, saldo: 0, custo: 0, timeline: [] };
            }

            console.log(`📋 [Reconciliation] ${movimentacoes.length} movimentações encontradas`);

            // 1.5. ORDENAÇÃO ROBUSTA (Memória)
            // Garante que ENTRADA venha antes de SAÍDA no mesmo instante para evitar saldo negativo indevido
            movimentacoes.sort((a, b) => {
                // 1. Data (String ISO ou Date)
                const dateA = new Date(a.data_movimentacao);
                const dateB = new Date(b.data_movimentacao);
                if (dateA - dateB !== 0) return dateA - dateB;

                // 2. Prioridade: ENTRADA < SAIDA
                if (a.tipo === 'ENTRADA' && b.tipo !== 'ENTRADA') return -1;
                if (a.tipo !== 'ENTRADA' && b.tipo === 'ENTRADA') return 1;

                // 3. Ordem de Criação
                return new Date(a.created_at) - new Date(b.created_at);
            });

            // 2. Buscar TODOS os custos de lançamentos de UMA VEZ (otimização!)
            const lancamentoIds = movimentacoes
                .filter(m => m.origem_tipo === 'COMPRA' && m.origem_id)
                .map(m => m.origem_id);

            const lancamentoCustos = await DataService.buscarCustosLancamentos(lancamentoIds);

            console.log(`💰 [Reconciliation] ${Object.keys(lancamentoCustos).length} custos de lançamentos mapeados`);

            // 3. Calcular sequencialmente em MEMÓRIA (sem queries!)
            let saldo = 0;
            let custo = 0;
            const timeline = [];

            for (const mov of movimentacoes) {
                const qtd = parseFloat(mov.quantidade);
                let custoMov = 0;

                // Resolver custo da movimentação
                if (mov.tipo === 'ENTRADA') {
                    if (mov.origem_id) {
                        // Buscar de lançamentos já mapeados
                        // CORREÇÃO: Normalizar chave e Fallback para custo_unitario salvo
                        const safeName = (mov.item_nome || '').trim().toUpperCase();
                        const key = `${mov.origem_id}_${safeName}`;

                        // Prioriza o recalculado do financeiro, mas aceita o snapshot se falhar ou se o financeiro retornar zero
                        const custoFinanceiro = lancamentoCustos[key];
                        const snapshotUnit = parseFloat(mov.custo_unitario || 0);
                        const snapshotTotal = mov.quantidade > 0 ? (parseFloat(mov.valor_total || 0) / parseFloat(mov.quantidade)) : 0;

                        // Se o custo financeiro for zero ou nulo, usamos o melhor snapshot disponível
                        if (!custoFinanceiro || custoFinanceiro === 0) {
                            custoMov = snapshotUnit || snapshotTotal || 0;
                            if (custoMov > 0) {
                                console.warn(`[Reconciliation] Usando fallback de snapshot para ${key}: R$ ${custoMov}`);
                            }
                        } else {
                            custoMov = custoFinanceiro;
                        }
                    } else {
                        // Outras entradas (Ajuste, etc) usam o próprio snapshot
                        custoMov = parseFloat(mov.custo_unitario || 0);
                    }
                }

                // Calcular novo estado
                const saldoAnterior = saldo;
                const custoAnterior = custo;

                if (mov.tipo === 'ENTRADA') {
                    // Calcular novo custo médio (com regra de RESET)
                    custo = Calculator.calcularCustoMedio(saldo, custo, qtd, custoMov);
                    saldo = Calculator.calcularNovoSaldo(saldo, 'ENTRADA', qtd);

                } else if (mov.tipo === 'SAIDA') {
                    saldo = Calculator.calcularNovoSaldo(saldo, 'SAIDA', qtd);
                    // Custo médio NÃO muda na saída
                }

                // Registrar no timeline
                timeline.push({
                    id: mov.id,
                    data: mov.data_movimentacao,
                    tipo: mov.tipo,
                    quantidade: qtd,
                    saldoAnterior: saldoAnterior.toFixed(2),
                    saldoNovo: saldo.toFixed(2),
                    custoAnterior: custoAnterior.toFixed(2),
                    custoNovo: custo.toFixed(2),
                    origemTipo: mov.origem_tipo,
                    origemId: mov.origem_id,
                    observacao: mov.observacao
                });
            }

            // 4. Atualizar estoque final (UMA ÚNICA QUERY)
            await DataService.atualizarEstoque(estoqueId, saldo, custo);

            const duration = (performance.now() - startTime).toFixed(1);
            console.log(`✅ [Reconciliation] Completo em ${duration}ms | Saldo Final: ${saldo.toFixed(2)} | Custo Médio: R$ ${custo.toFixed(2)}`);
            console.log(`📊 [Reconciliation] Timeline de ${timeline.length} eventos gerada\n`);

            return {
                success: true,
                saldo: saldo,
                custo: custo,
                timeline: timeline,
                duration: duration
            };

        } catch (error) {
            console.error(`❌ [Reconciliation] Erro ao recalcular estoque:`, error);
            return {
                success: false,
                error: error.message,
                saldo: null,
                custo: null
            };
        }
    },

    /**
     * Recalcula múltiplos itens em lote (útil para migrações)
     * 
     * @param {Array<string>} estoqueIds - Lista de IDs de estoque
     * @param {function} progressCallback - Callback para progresso (opcional)
     * @returns {Promise<Object>} { totalProcessados, sucessos, falhas }
     */
    recalcularLote: async function (estoqueIds, progressCallback = null) {
        console.log(`🔄 [Reconciliation] Recálculo em lote: ${estoqueIds.length} itens`);

        let sucessos = 0;
        let falhas = 0;

        for (let i = 0; i < estoqueIds.length; i++) {
            const id = estoqueIds[i];

            try {
                const result = await this.recalcularEstoque(id);
                if (result.success) {
                    sucessos++;
                } else {
                    falhas++;
                }
            } catch (error) {
                console.error(`[Reconciliation] Falha ao processar ${id}:`, error);
                falhas++;
            }

            // Callback de progresso
            if (progressCallback) {
                progressCallback({
                    atual: i + 1,
                    total: estoqueIds.length,
                    percentual: ((i + 1) / estoqueIds.length * 100).toFixed(1)
                });
            }
        }

        console.log(`✅ [Reconciliation] Lote completo: ${sucessos} sucessos, ${falhas} falhas`);

        return {
            totalProcessados: estoqueIds.length,
            sucessos: sucessos,
            falhas: falhas
        };
    }
};
